import * as React from 'react';
import {observer} from 'mobx-react';
import styled from 'styled-components';
import MenuItem from './components/MenuItem';
import ControlButton from './components/MenuItem/components/ControlButton';
import SidebarContext from 'components/Sidebar/SidebarContext';
import UserStore, {Role} from 'stores/UserStore';
import {
  IconDashboard,
  IconCourses,
  IconLessons,
  IconWebinar,
  IconTasks,
  IconPayments,
  IconStudents,
  IconPlus,
  IconLibrary, IconFood, IconWorkout,
} from 'icons';
import MenuItemTariffs from "./components/MenuItemTariffs";
import scrollStyles from "helpers/css/scroll";
import {ThemeSwitch} from '../../../ThemeSwitch';

const Container = styled.nav<{
  isToggle?: boolean;
}>`
  box-sizing: border-box;
  padding: ${({isToggle}) => isToggle 
      ? '0 12px 20px' 
      : '0 8px 20px'};
  display: flex;
  flex-direction: column;
`;

const BlueControl = styled.div`
  color: ${({theme}) => `
    ${theme.colors.primary};
  `};
`;

const Overflow = styled.div`
  overflow-y: auto;
`;

/**
 * This component renders the sidebar menu.
 * @return {React.ReactElement}
 * @constructor
 */
function Menu(): React.ReactElement {
  const context = React.useContext(SidebarContext);

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    switch (UserStore.state?.role) {
      case Role.Student: return (
        <React.Fragment>
          <MenuItem
            routeName="courses"
            icon={<IconCourses />}
            label="Мои курсы"
          />
          {UserStore.state?.favoriteCourseId && (
            <MenuItem
              routeName={null}
              href={`/courses/${UserStore.state?.favoriteCourseId}/program`}
              icon={<IconCourses />}
              label="Расписание"
            >
              <MenuItem
                routeName="courses"
                label="1 день (Пн, 25 октября)"
              >
                <MenuItem
                  routeName="courses"
                  label="Технологии современного ..."
                />
                <MenuItem
                  routeName="courses"
                  label="Тестирование"
                />
                <MenuItem
                  routeName="courses"
                  label="Трансляция"
                />
              </MenuItem>
              <MenuItem
                routeName="courses"
                label="2 день (Пн, 26 октября)"
              />
            </MenuItem>
          )}
          <MenuItem
            routeName="payments"
            icon={<IconPayments />}
            label="История покупок"
          />
        </React.Fragment>
      );
      case Role.Teacher: return (
        <React.Fragment>
          <MenuItem
            href={'dashboard'}
            routeName="dashboard"
            icon={<IconDashboard />}
            label="Дашборд"
          />
          <MenuItem
            href={'courses'}
            routeName="courses"
            icon={<IconCourses />}
            controls={(
              <BlueControl>
                <ControlButton routeName={"courses.create"}>
                  <IconPlus />
                </ControlButton>
              </BlueControl>
            )}
            label="Курсы"
          />
          <MenuItem
            href="lessons"
            routeName="lessons"
            icon={<IconLessons />}
            controls={(
              <BlueControl>
                <ControlButton routeName={"lessons.create"}>
                  <IconPlus />
                </ControlButton>
              </BlueControl>
            )}
            label="Уроки"
          />
          <MenuItem
            href="webinars"
            routeName="webinars"
            icon={<IconWebinar />}
            controls={(
              <BlueControl>
                <ControlButton routeName={"webinars.create"}>
                  <IconPlus />
                </ControlButton>
              </BlueControl>
            )}
            label="Трансляции"
          />
          <MenuItem
            icon={<IconTasks />}
            label="Задания"
          >
            <MenuItem
              href="tasks"
              routeName="tasks"
              controls={(
                <BlueControl>
                  <ControlButton
                    routeName={"tasks.create"}>
                    <IconPlus />
                  </ControlButton>
                </BlueControl>
              )}
              label="Список заданий"
            />
            <MenuItem
              href="tasks/view"
              routeName="tasks.view"
              label="На проверку"
            />
          </MenuItem>
          <MenuItem
            icon={<IconFood />}
            label="Питание"
          >
            <MenuItem
              href="foods"
              routeName="foods"
              controls={(
                <BlueControl>
                  <ControlButton
                    routeName={"foods.create"}>
                    <IconPlus />
                  </ControlButton>
                </BlueControl>
              )}
              label="Программы питания"
            />
            <MenuItem
              href="recipes"
              routeName="recipes"
              controls={(
                <BlueControl>
                  <ControlButton
                    routeName={"recipes.create"}>
                    <IconPlus />
                  </ControlButton>
                </BlueControl>
              )}
              label="Рецепты"
            />
            <MenuItem
              href="ingredients"
              routeName="ingredients"
              controls={(
                <BlueControl>
                  <ControlButton
                    routeName={"ingredients.create"}>
                    <IconPlus />
                  </ControlButton>
                </BlueControl>
              )}
              label="Ингредиенты"
            />
          </MenuItem>
          <MenuItem
            href="trainings"
            icon={<IconWorkout />}
            label="Тренеровки"
          >
            <MenuItem
              href="trainings"
              routeName="trainings"
              controls={(
                <BlueControl>
                  <ControlButton
                    routeName={"trainings.create"}>
                    <IconPlus />
                  </ControlButton>
                </BlueControl>
              )}
              label="Программы тренеровок"
            />
            <MenuItem
              href="exercises"
              routeName="exercises"
              controls={(
                <BlueControl>
                  <ControlButton
                    routeName={"exercises.create"}>
                    <IconPlus />
                  </ControlButton>
                </BlueControl>
              )}
              label="Упражнения"
            />
          </MenuItem>
          <MenuItem
            href="payments"
            routeName="payments"
            icon={<IconPayments />}
            label="Оплаты"
          />
          <MenuItem
            href="students"
            routeName="students"
            icon={<IconStudents />}
            label="Студенты"
          />
          <MenuItem
            href="library"
            routeName="library"
            icon={<IconLibrary />}
            label="Библиотека"
          />
          {context?.isToggle && <MenuItemTariffs />}
        </React.Fragment>
      );
      default: return (
        <React.Fragment />
      );
    }
  }

  return (
    <Overflow>
      <Container isToggle={context?.isToggle}>
        {renderContent()}
        <ThemeSwitch />
      </Container>
    </Overflow>
  );
}

export default observer(Menu);
